import { CloseButton, Group, Select, Text } from "@mantine/core";
import { useWindowEvent } from "@mantine/hooks";
import { useState, type ReactNode } from "react";

import type { CatalogAudioModel, Voice } from "../../api/generated/model";
import { CreateVoice, type CreateVoiceProps } from "./CreateVoice";
import { NO_FILTER, VoiceFilters, type VoiceFilter } from "./VoiceFilters";
import { VoiceList, VoiceRows } from "./VoiceList";
import classes from "./voices.module.css";

export type VoiceSource = "global" | "recommended" | "team" | "create";

/**
 * Live's sources, in its order and words (`voice-select/constants.ts`): "Recommended Voice" only
 * where the form has one, an actor's own voice (`utils.ts`).
 */
const SOURCES: { value: VoiceSource; label: string }[] = [
  { value: "global", label: "Global Voices" },
  { value: "recommended", label: "Recommended Voice" },
  { value: "team", label: "Custom Voices" },
  { value: "create", label: "Create Voice" },
];

export interface VoicePanelProps {
  teamId: string;
  brandId: string;
  /** The voice the form holds, or none (an actor's own is known by its id and kind alone). */
  chosen: Pick<Voice, "id" | "kind"> | null;
  /** A voice chosen in a list, or made and saved: the panel stays open, as live's. */
  onChoose: (voice: Voice) => void;
  /** Why this form cannot use a voice, shown on its disabled row, or null (Kling 4.0's,
   * G2-kling-element-voice-picker). */
  whyNot?: ((voice: Voice) => string | null) | undefined;
  onClose: () => void;
  /** The catalog's voice design model, or none: Design is Coming soon. */
  designModel: CatalogAudioModel | undefined;
  /** An actor's own voice: live's "Recommended Voice" source, where it starts when chosen. */
  recommended?: Voice | null;
  /** Above the source: the Talking Head's actor and its voice (live's `media-voice-tile`). */
  head?: ReactNode;
  /** At the bottom: the Talking Head's "Advanced" (live's `voice-advanced-settings`). */
  foot?: ReactNode;
  /** The sources a form offers, in live's order: Your Personal Clone's Custom Voices and Create
   * Voice alone; every one by default. */
  only?: readonly VoiceSource[];
  /** Create Voice's tabs, where a form words them its own way (Your Personal Clone's "Record
   * Audio" and "Upload Audio", no Design). */
  createTabs?: CreateVoiceProps["tabs"];
}

/**
 * Live's "Voice Settings" (#236, `voice-settings-panel` and `voice-select`, captures 03, L02 to
 * L02f, 12c and L09g): the title and its x, the form's own head, the source (Global Voices,
 * Recommended Voice where there is one, Custom Voices, Create Voice, starting where the chosen
 * voice comes from), live's filter on the global voices, the list or Create Voice, and the form's
 * own foot. It keeps nothing once closed, as live's.
 */
export function VoicePanel(props: VoicePanelProps) {
  const { teamId, brandId, chosen, onChoose, onClose, designModel, head, foot, whyNot } = props;
  const recommended = props.recommended ?? null;
  const only = props.only ?? SOURCES.map((each) => each.value);
  const [source, setSource] = useState<VoiceSource>(() => {
    if (recommended !== null && chosen?.id === recommended.id) return "recommended";
    const own = chosen?.kind === "team" ? "team" : "global";
    return only.includes(own) ? own : (only[0] ?? "global");
  });
  const sources = SOURCES.filter(
    (each) => only.includes(each.value) && (each.value !== "recommended" || recommended !== null),
  );
  const [filter, setFilter] = useState<VoiceFilter>(NO_FILTER);
  // Escape closes it, as the form's other layers; not while a list inside it is open, whose own
  // Escape closes the list first.
  useWindowEvent("keydown", (event) => {
    if (event.key !== "Escape" || event.defaultPrevented) return;
    const at = event.target instanceof Element ? event.target : null;
    if (at?.closest('[aria-expanded="true"], [role="listbox"], [role="menu"], [role="dialog"]')) return;
    onClose();
  });
  return (
    <section className={classes.panel} aria-label="Voice Settings">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.title}>Voice Settings</Text>
        <CloseButton aria-label="Close the voice settings" onClick={onClose} />
      </Group>
      {head}
      <Group gap={8} wrap="nowrap">
        <Select
          aria-label="Voices"
          data={sources}
          value={source}
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
          onChange={(next) => {
            if (next !== null) setSource(next as VoiceSource);
          }}
        />
        {source === "global" ? <VoiceFilters applied={filter} onApply={setFilter} /> : null}
      </Group>
      {source === "global" ? (
        <VoiceList
          teamId={teamId}
          brandId={brandId}
          params={{
            kind: "global",
            ...(filter.gender === null ? {} : { gender: filter.gender }),
            ...(filter.age === null ? {} : { age: filter.age }),
          }}
          chosen={chosen?.id ?? null}
          onChoose={onChoose}
          empty="No global voices found"
          whyNot={whyNot}
        />
      ) : source === "recommended" ? (
        // Live's one row: the actor's own voice (`recommended-voice`).
        <VoiceRows
          voices={recommended === null ? [] : [recommended]}
          chosen={chosen?.id ?? null}
          onChoose={onChoose}
          empty="No recommended voice"
          onLost={() => undefined}
        />
      ) : source === "team" ? (
        <VoiceList
          teamId={teamId}
          brandId={brandId}
          params={{ kind: "team" }}
          chosen={chosen?.id ?? null}
          onChoose={onChoose}
          empty="No custom voices found"
          whyNot={whyNot}
        />
      ) : (
        <CreateVoice
          teamId={teamId}
          brandId={brandId}
          designModel={designModel}
          onCreated={onChoose}
          {...(props.createTabs === undefined ? {} : { tabs: props.createTabs })}
        />
      )}
      {foot}
    </section>
  );
}
